import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {useDispatch} from 'react-redux';

import {useCountries} from '../hooks/useCountries';
import {countryChanged} from '../slices/countryChanged';
import {Country} from '../types/geolocations';
import {hasAccess} from '../utils/user-access';

interface CountryContextType {
  selectedCountry: Country | null;
  setSelectedCountry: (country: Country | null) => void;
  countries: Country[];
  isLoading: boolean;
  error: unknown;
}

const CountryContext = createContext<CountryContextType | undefined>(undefined);

const STORAGE_KEY = 'selectedCountryId';

interface CountryProviderProps {
  children: ReactNode;
}

export function CountryProvider({children}: CountryProviderProps) {
  const {data: countries = [], isLoading, error} = useCountries();
  const dispatch = useDispatch();
  const [selectedCountry, setSelectedCountryState] = useState<Country | null>(
    null,
  );

  useEffect(() => {
    if (!countries.length) return;

    const savedCountryId = localStorage.getItem(STORAGE_KEY);
    if (savedCountryId) {
      const country = countries.find(c => c.id === Number(savedCountryId));
      if (country) {
        setSelectedCountryState(country);
        return;
      }
    }

    const defaultCountry = getDefaultCountry(countries);
    if (defaultCountry) {
      setSelectedCountryState(defaultCountry);
      localStorage.setItem(STORAGE_KEY, defaultCountry.id.toString());
    }
  }, [countries]);

  const getDefaultCountry = (availableCountries: Country[]): Country | null => {
    if (!availableCountries.length) return null;

    const assignedDefaultCountry = availableCountries.find(
      country => country.isDefault,
    );
    if (assignedDefaultCountry) return assignedDefaultCountry;

    if (hasAccess('super-admin')) {
      const guatemala = availableCountries.find(c => c.id === 502);
      if (guatemala) return guatemala;
    }

    return availableCountries[0];
  };

  // Ref para comparar el país actual sin meterlo en las deps del useCallback.
  const selectedCountryRef = useRef(selectedCountry);
  selectedCountryRef.current = selectedCountry;

  const setSelectedCountry = useCallback(
    (country: Country | null) => {
      const changed = country?.id !== selectedCountryRef.current?.id;
      setSelectedCountryState(country);
      if (country) {
        localStorage.setItem(STORAGE_KEY, country.id.toString());
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
      if (changed) {
        dispatch(countryChanged());
      }
    },
    [dispatch],
  );

  const value = useMemo(
    () => ({
      selectedCountry,
      setSelectedCountry,
      countries,
      isLoading,
      error,
    }),
    [selectedCountry, setSelectedCountry, countries, isLoading, error],
  );

  return (
    <CountryContext.Provider value={value}>{children}</CountryContext.Provider>
  );
}

export function useSelectedCountry() {
  const context = useContext(CountryContext);
  if (context === undefined) {
    throw new Error(
      'useSelectedCountry debe ser utilizado dentro de un CountryProvider',
    );
  }
  return context;
}
